import {useEffect, RefObject} from 'react';

const OBSERVER_OPTIONS = {
    childList: true,
    subtree: true,
    attributes: true,
    characterData: true
};

type ElementRef = RefObject<HTMLElement | null>;
type ElementNodes = NodeListOf<Element> | undefined;
type ElementMap = Map<string, Array<HTMLElement>>;

function useSyncElementsHeight(parentElRef: ElementRef) {
    useEffect(() => {
        if (!parentElRef?.current) return;

        const parentEl = parentElRef.current;

        function groupElements(allElems: ElementNodes) {
            const initialValue: ElementMap = new Map();

            function handler(acc: ElementMap, elm: Element) {
                const key = elm.getAttribute('data-sync-height');

                if (!acc.has(key!)) acc.set(key!, []);

                acc.get(key!)?.push(elm as HTMLElement);

                return acc;
            }

            return Array.from(allElems || []).reduce(handler, initialValue);
        }

        function syncHeights(elems: Array<HTMLElement>) {
            const maxHeight = Math.max(...elems.map((elm) => elm.clientHeight));
            elems.forEach((elm) => (elm.style.minHeight = `${maxHeight}px`));
        }

        function mutationHandler() {
            const allElems = parentEl?.querySelectorAll('[data-sync-height]');
            const groupedElems = groupElements(allElems);
            groupedElems?.forEach(syncHeights);
        }

        mutationHandler();

        let observer = new MutationObserver(mutationHandler);
        observer.observe(parentEl, OBSERVER_OPTIONS);

        return () => observer?.disconnect();
    }, [parentElRef]);
}

export {useSyncElementsHeight};
